import React from "react";
import { CONSTANTS, NOTES, DRILLING_GLOSSARY, MUD_GLOSSARY, CEMENT_GLOSSARY, WELLCONTROL_GLOSSARY, CASING_GLOSSARY, DIRECTIONAL_GLOSSARY, STUCK_FISHING_GLOSSARY, VERIFICATION_REMINDERS, SAFETY_NOTE, TRUST_NOTE } from "@/lib/fieldData";
import { SafetyBanner } from "@/components/SafetyBanner";
import { BookOpen } from "lucide-react";

function Glossary({ title, items, testid }) {
  return (
    <section className="card-rs p-4" data-testid={testid}>
      <div className="label-up text-[var(--rs-amber)] mb-3">{title}</div>
      <dl className="space-y-2">
        {items.map((g) => (
          <div key={g.term} className="grid grid-cols-[80px_1fr] gap-3">
            <dt className="font-mono-rs text-xs text-[var(--rs-text)] mt-0.5">{g.term}</dt>
            <dd className="text-xs text-[var(--rs-text-2)] leading-relaxed">{g.def}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default function FieldData() {
  return (
    <div className="flex flex-col gap-5" data-testid="field-data-page">
      <section>
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)] inline-flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" /> Reference
        </div>
        <h2 className="font-heading font-black text-3xl tracking-tight mt-1">Field Data Library</h2>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5">Original notes, constants, and glossaries.</p>
      </section>

      <section className="card-rs p-4">
        <div className="label-up text-[var(--rs-amber)] mb-3">Common Oilfield Constants</div>
        <div className="grid grid-cols-1 gap-2">
          {CONSTANTS.map((c) => (
            <div key={c.label} className="flex justify-between border-b last:border-0 border-[var(--rs-border)] pb-1.5">
              <span className="text-xs text-[var(--rs-text-2)]">{c.label}</span>
              <span className="font-mono-rs text-xs text-[var(--rs-text)]">{c.value}</span>
            </div>
          ))}
        </div>
      </section>

      {NOTES.map((n) => (
        <section key={n.title} className="card-rs p-4">
          <div className="label-up text-[var(--rs-amber)] mb-2">{n.title}</div>
          <p className="text-xs text-[var(--rs-text-2)] leading-relaxed">{n.body}</p>
        </section>
      ))}

      <Glossary title="Drilling Abbreviations" items={DRILLING_GLOSSARY} testid="glossary-drilling" />
      <Glossary title="Mud Terms" items={MUD_GLOSSARY} testid="glossary-mud" />
      <Glossary title="Cementing Terms" items={CEMENT_GLOSSARY} testid="glossary-cement" />
      <Glossary title="Well-Control Terms" items={WELLCONTROL_GLOSSARY} testid="glossary-wellcontrol" />
      <Glossary title="Casing & Tubing Terms" items={CASING_GLOSSARY} testid="glossary-casing" />
      <Glossary title="Directional Terms" items={DIRECTIONAL_GLOSSARY} testid="glossary-directional" />
      <Glossary title="Stuck Pipe & Fishing Terms" items={STUCK_FISHING_GLOSSARY} testid="glossary-stuck" />

      <section className="card-rs p-4" data-testid="verification-reminders">
        <div className="label-up text-[var(--rs-amber)] mb-3">Verification Reminders</div>
        <ul className="space-y-2">
          {VERIFICATION_REMINDERS.map((v, i) => (
            <li key={i} className="flex gap-2 text-xs text-[var(--rs-text-2)] leading-relaxed">
              <span className="font-mono-rs text-[var(--rs-amber)] shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span>{v}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="card-rs p-4" data-testid="trust-note">
        <div className="label-up text-[var(--rs-amber)] mb-2">Built For The Field</div>
        <p className="text-xs text-[var(--rs-text-2)] leading-relaxed">{TRUST_NOTE}</p>
      </section>

      <section className="card-rs p-4" data-testid="safety-page-note">
        <div className="label-up text-[var(--rs-danger)] mb-2">{SAFETY_NOTE.title}</div>
        <p className="text-xs text-[var(--rs-text-2)] leading-relaxed">{SAFETY_NOTE.body}</p>
      </section>

      <SafetyBanner />
      <SafetyBanner wellControl />
    </div>
  );
}
